import { useMutation } from "@tanstack/react-query";

import {
  type FreedompayPaymentStartSuccessPayload,
  type FreedompayPaymentStartRequestPayload
} from "~/types/events";
import { useLatestMutation } from "~/utils/tanstack-query";
import { getErrorMessage } from "@repo/system";
import { DisplayError, DisplayErrorCodeEnum } from "~/utils/display-error";

import { awaitNativeEventOnce } from "../use-native-event-promise/use-native-event-promise";

import { POI_TIMEOUT_MS } from "./constants";

const START_POI_PAYMENT_MUTATION_KEY = "freedompay-start-poi-payment";

export const useStartPoiPayment = () => {
  return useMutation({
    mutationKey: [START_POI_PAYMENT_MUTATION_KEY],
    mutationFn: async (args: FreedompayPaymentStartRequestPayload) => {
      try {
        return await awaitNativeEventOnce({
          timeoutMs: POI_TIMEOUT_MS,
          successEvent: "FREEDOMPAY_PAYMENT_START_SUCCESS",
          errorEvent: "FREEDOMPAY_PAYMENT_START_ERROR",
          sendEvent: "FREEDOMPAY_PAYMENT_START_REQUEST",
          payload: args
        });
      } catch (error) {
        const message = getErrorMessage(error);
        if (message === "UserCancel") {
          throw new DisplayError(DisplayErrorCodeEnum.POI_USER_CANCEL);
        }

        if (message === "Operation timed out") {
          // Fire-and-forget cancel to release the terminal
          awaitNativeEventOnce({
            timeoutMs: POI_TIMEOUT_MS,
            successEvent: "FREEDOMPAY_PAYMENT_CANCEL_SUCCESS",
            errorEvent: "FREEDOMPAY_PAYMENT_CANCEL_ERROR",
            sendEvent: "FREEDOMPAY_PAYMENT_CANCEL_REQUEST"
          }).catch(() => {
            // Intentionally ignored — best-effort cancel
          });

          throw new DisplayError(DisplayErrorCodeEnum.POI_TIMEOUT);
        }

        throw error;
      }
    }
  });
};

export function useLatestStartPoiPayment() {
  return useLatestMutation<FreedompayPaymentStartSuccessPayload>({
    key: [START_POI_PAYMENT_MUTATION_KEY]
  });
}
